import { useEffect, useRef } from 'react';

const blogPosts = [
  {
    id: 1,
    category: 'Inspiracija',
    title: 'Kako odabrati savršen kućni broj za vaš dom',
    excerpt: 'Od klasičnih do modernih dizajna — saveti za izbor kućnog broja koji se uklapa u stil vašeg doma. Materijali, boje i fontovi koji prave razliku.',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20house%20number%20sign%20on%20modern%20home%20exterior%20wall%2C%20brushed%20metal%20finish%20with%20warm%20sunlight%2C%20minimalist%20architectural%20design%2C%20green%20garden%20background%2C%20editorial%20photography%20with%20soft%20shadows&width=600&height=400&seq=blog-1&orientation=landscape',
    date: '15. maj 2026',
    readTime: '4 min',
  },
  {
    id: 2,
    category: 'Vodič',
    title: 'Najbolji načini za montažu kućnih brojeva',
    excerpt: 'Step-by-step vodič za postavljanje kućnih brojeva na različite površine — drvo, cigla, beton, metal i staklo. Sve što vam je potrebno za sigurnu montažu.',
    image: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20hands%20installing%20modern%20house%20number%20on%20brick%20wall%20with%20drill%20and%20screws%2C%20warm%20daylight%2C%20DIY%20home%20improvement%20scene%2C%20editorial%20photography%20with%20natural%20lighting%20and%20soft%20shadows&width=600&height=400&seq=blog-2&orientation=landscape',
    date: '8. maj 2026',
    readTime: '6 min',
  },
  {
    id: 3,
    category: 'Trendovi',
    title: 'Top 5 trendova za kućne brojeve u 2026.',
    excerpt: 'Otkrijte najpopularnije stilove kućnih brojeva ove godine — od retro vintage dizajna do futurističkih LED osvetljenih oznaka. Šta je u trendu?',
    image: 'https://readdy.ai/api/search-image?query=Collection%20of%20modern%20house%20number%20designs%20arranged%20on%20light%20wooden%20surface%2C%20various%20materials%20including%20brass%20wood%20and%20acrylic%2C%20flat%20lay%20photography%20with%20warm%20natural%20light%20and%20clean%20minimalist%20composition&width=600&height=400&seq=blog-3&orientation=landscape',
    date: '1. maj 2026',
    readTime: '5 min',
  },
];

export default function BlogSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal');
            els.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-in');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="blog" ref={sectionRef} className="w-full py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-[1450px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-10 md:mb-12">
          <div>
            <h2 className="reveal opacity-0 translate-y-4 transition-all duration-700 ease-out font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
              Blog
            </h2>
            <p className="reveal opacity-0 translate-y-4 transition-all duration-700 delay-100 ease-out text-base text-foreground-700 max-w-lg leading-relaxed">
              Saveti, inspiracija i vodiči za izbor i postavljanje kućnih brojeva i oznaka.
            </p>
          </div>
          <a
            href="/"
            className="reveal opacity-0 translate-y-4 transition-all duration-700 delay-200 ease-out mt-4 sm:mt-0 text-sm font-label font-medium text-amber-600 hover:text-amber-700 transition-colors flex items-center gap-1 whitespace-nowrap"
          >
            Svi članci
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {blogPosts.map((post, i) => (
            <a
              key={post.id}
              href="/"
              className="reveal opacity-0 translate-y-4 transition-all duration-700 ease-out group block bg-white rounded-lg overflow-hidden border border-background-200 hover:border-amber-300 transition-all duration-300"
              style={{ transitionDelay: `${(i + 3) * 100}ms` }}
            >
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-label font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    {post.category}
                  </span>
                  <span className="text-xs font-label text-foreground-600">
                    {post.readTime} čitanja
                  </span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2 group-hover:text-amber-600 transition-colors leading-snug">
                  {post.title}
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-background-200">
                  <span className="text-xs font-label text-foreground-600">
                    {post.date}
                  </span>
                  <span className="text-sm font-label font-medium text-amber-600 flex items-center gap-1">
                    Pročitaj više
                    <i className="ri-arrow-right-line text-xs" />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>

      <style>{`
        .reveal.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
}